import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import collectiveApi from 'modules/common/collectiveApi';

const PATH = '/bookkeeping/v2/bank-statements/assign-account/';

/**
 * Only a 503 is retried: the call verifies the id against live Tight accounts, so a 503 means
 * nothing was recorded, while 400 and 404 will fail the same way again. Bounded because an
 * unassigned file loses the guarantee, not the upload.
 */
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

/**
 * The account as the endpoint reports it back. Not the shape the server persists against the
 * document, which carries a bare numeric id — two shapes for two jobs, not a drift.
 * Unexported: the caller discards it and reads the verdict back from
 * `useStatementUploadStatuses`, which recomputes it rather than echoing what was assigned.
 */
interface RecordedStatementAccount {
    bank_account_id: string;
    account_mask: string;
    institution_name: string;
    account_type: string;
}

interface AssignStatementAccountParams {
    memberId: number;
    /** From the document upload response. */
    memberDocumentUuid: string;
    /** The card the file was dropped on. v6 `bka_` form or bare numeric; both are accepted. */
    bankAccountId: string;
}

/**
 * Record which account card an uploaded statement was dropped on.
 *
 * Called after each upload rather than as part of it, since `POST /documents/member-documents/`
 * is shared by every upload surface and has no business knowing about bank accounts.
 *
 * It buys two properties, both lost for a file without it. The tracker row is written in the
 * same transaction that flips the job to `SUCCESS`, so a missing row means "read and could not
 * file" rather than "still landing". And the wrong-account rule moves server-side, so the
 * rejection the member sees and the credit the database records agree.
 *
 * Idempotent — re-sending replaces the assignment, which a retry after a lost response needs.
 */
export default function useAssignStatementAccount() {
    return useMutation<RecordedStatementAccount, AxiosError, AssignStatementAccountParams>({
        mutationFn: async ({ memberId, memberDocumentUuid, bankAccountId }) => {
            const { data } = await collectiveApi.post<RecordedStatementAccount>(PATH, {
                member_id: memberId,
                member_document_uuid: memberDocumentUuid,
                bank_account_id: bankAccountId,
            });
            return data;
        },
        retry: (failureCount, error) => error.response?.status === 503 && failureCount < MAX_ATTEMPTS - 1,
        retryDelay: RETRY_DELAY_MS,
    });
}
